import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { DonationRow } from '../lib/donations';
import {
  donationResourceLabel,
  donationResourceRef,
  donationStatusKey,
  donationTargetLabel,
} from '../lib/donationView';
import { ossProjectUrl } from '../lib/publicOss';
import { absolute } from '../lib/time';
import { DataTable, type DataTableColumn } from './DataTable';
import { Button, buttonClass } from './ui/Button';

// The donor's gifts — the ones the caller GAVE — as a dense data table (spec
// §3.15, arc 101 slice 11, plan doc 05). Presenter — the route owns the
// accumulated rows, the detach call, its error and which rows are mid-detach. It
// asks per row rather than taking one id: two detaches run independently.
//
// Parked and pending rows are listed beside live ones on purpose: a gift that
// vanished when the subscription lapsed is exactly what a donor needs to see.
//
// **Three statuses, not two.** `active: false` means different things and the
// donor must be able to tell them apart: a row with a `revoked_iso` was PARKED
// (they detached it, or their subscription lapsed and we stopped operating it),
// while one without is a `money` gift Stripe has not confirmed yet. Collapsing
// them would tell a donor mid-checkout that their gift was cancelled.
//
// The project links to its PUBLIC page, not to `/repos/:full_name`: a donor funds
// repos they do not own, and the account-scoped route would 404 for them.

export type DonationsTableProps = {
  readonly rows: readonly DonationRow[];
  /** True while THIS gift's detach is in flight — keyed, since rows detach independently. */
  readonly isDetaching: (id: string) => boolean;
  readonly onDetach: (row: DonationRow) => void;
};

export function DonationsTable(props: DonationsTableProps): JSX.Element {
  const columns: readonly DataTableColumn<DonationRow>[] = [
    {
      key: 'kind',
      header: t('donations.col_kind'),
      cell: (row) => t(`donations.kind_${row.kind}`),
    },
    {
      key: 'project',
      header: t('donations.col_project'),
      cell: (row) => (
        <Show
          when={row.target_kind === 'project' ? row.target_repo_full_name : undefined}
          fallback={donationTargetLabel(row)}
        >
          {(name) => (
            <a
              class={buttonClass({ variant: 'link' })}
              href={ossProjectUrl(name())}
              rel="noreferrer"
            >
              {name()}
            </a>
          )}
        </Show>
      ),
    },
    {
      key: 'resource',
      header: t('donations.col_resource'),
      mono: true,
      cell: (row) => <span title={donationResourceRef(row)}>{donationResourceLabel(row)}</span>,
    },
    {
      key: 'status',
      header: t('donations.col_status'),
      cell: (row) => <span class="badge badge--outline">{t(donationStatusKey(row))}</span>,
    },
    {
      key: 'created',
      header: t('donations.col_created'),
      cell: (row) => absolute(row.created_iso),
    },
    {
      key: 'actions',
      header: t('donations.col_actions'),
      srOnlyHeader: true,
      align: 'end',
      // Live → Detach. Pending (unpaid, never revoked) → Cancel pledge: the same
      // `DELETE /v1/donations/:id` withdraws an unpaid `money` pledge, and an
      // abandoned checkout otherwise left an "Awaiting payment" row with no
      // control at all (plan 101 slice 17 row 17-8). Parked → nothing.
      cell: (row) => (
        <Show when={row.active || row.revoked_iso === null}>
          <div class="data-table__actions">
            <Button
              variant="outline"
              size="sm"
              disabled={props.isDetaching(row.id)}
              aria-label={t(row.active ? 'donations.detach_aria' : 'donations.cancel_pledge_aria', {
                kind: t(`donations.kind_${row.kind}`),
                project: donationTargetLabel(row),
              })}
              onClick={() => props.onDetach(row)}
            >
              {t(
                props.isDetaching(row.id)
                  ? 'donations.detaching'
                  : row.active
                    ? 'donations.detach'
                    : 'donations.cancel_pledge',
              )}
            </Button>
          </div>
        </Show>
      ),
    },
  ];

  return (
    <DataTable
      label={t('donations.table_label')}
      class="donations"
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.id}
    />
  );
}
